import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

export default function Shorten() {
  const { isAuthenticated } = useAuth();

  const [form, setForm] = useState({ originalUrl: '', shortCode: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.originalUrl.trim()) {
      errs.originalUrl = 'Please enter a URL.';
    } else {
      try { new URL(form.originalUrl); }
      catch { errs.originalUrl = 'Please enter a valid URL (include https://).'; }
    }
    if (form.shortCode && !/^[a-zA-Z0-9_-]+$/.test(form.shortCode)) {
      errs.shortCode = 'Only letters, numbers, hyphens and underscores allowed.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setResult(null);

    try {
      const { data } = await axios.post(`${API}/urls`, {
        originalUrl: form.originalUrl,
        customCode: form.shortCode || undefined,
      });

      setResult(data.data);
      setForm({ originalUrl: '', shortCode: '' });
      toast.success('Short URL created! 🎉');
    } catch (err) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ─── Copy ─────────────────────────────────────────────────────────────────
  const handleCopy = async () => {
    await navigator.clipboard.writeText(result.shortUrl);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-purple-50 flex items-start justify-center py-16 px-4">
      <div className="w-full max-w-lg animate-slide-up">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block bg-brand-100 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            URL Shortener
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-2">
            Make it short. Make it yours.
          </h1>
          <p className="text-gray-500 text-base">
            Paste any long URL below and get a clean short link instantly.
          </p>
        </div>

        {/* Form Card */}
        <div className="card shadow-lg border-0">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">

            {/* Original URL */}
            <div>
              <label className="label">Long URL *</label>
              <input
                type="text"
                className={`input ${errors.originalUrl ? 'border-red-400 ring-2 ring-red-200' : ''}`}
                placeholder="https://example.com/very-long-url..."
                value={form.originalUrl}
                onChange={(e) => { setForm({ ...form, originalUrl: e.target.value }); setErrors({ ...errors, originalUrl: '' }); }}
              />
              {errors.originalUrl && (
                <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">⚠ {errors.originalUrl}</p>
              )}
            </div>

            {/* Custom Code */}
            <div>
              <label className="label">
                Custom Short Code
                <span className="ml-2 text-xs text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none">
                  bitlinks.app/
                </span>
                <input
                  type="text"
                  className={`input pl-[6.5rem] ${errors.shortCode ? 'border-red-400 ring-2 ring-red-200' : ''}`}
                  placeholder="my-link"
                  value={form.shortCode}
                  onChange={(e) => { setForm({ ...form, shortCode: e.target.value }); setErrors({ ...errors, shortCode: '' }); }}
                />
              </div>
              {errors.shortCode ? (
                <p className="mt-1.5 text-xs text-red-500">⚠ {errors.shortCode}</p>
              ) : (
                <p className="mt-1.5 text-xs text-gray-400">Leave blank to auto-generate a random code.</p>
              )}
            </div>

            <button type="submit" className="btn-primary w-full py-3 text-base mt-1" disabled={loading}>
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Generating...
                </>
              ) : 'Shorten URL →'}
            </button>
          </form>

          {/* Result */}
          {result && (
            <div className="mt-6 p-4 bg-brand-50 border border-brand-200 rounded-xl animate-slide-up">
              <p className="text-xs font-semibold text-brand-600 uppercase tracking-wider mb-2">Your Short Link</p>
              <div className="flex items-center gap-2">
                <a
                  href={result.shortUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-brand-700 font-bold text-base truncate hover:underline"
                >
                  {result.shortUrl}
                </a>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                    copied ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-700 hover:bg-brand-200'
                  }`}
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2 truncate">→ {result.originalUrl}</p>
            </div>
          )}
        </div>

        {/* Auth nudge */}
        {!isAuthenticated && (
          <div className="mt-4 text-center p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <p className="text-sm text-gray-500">
              Want to track your links?{' '}
              <Link to="/register" className="text-brand-600 font-semibold hover:underline">
                Create a free account →
              </Link>
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
